import React from "react";

import { ManifestIntegrationProvider } from "src/api/connectApi/manifestTypes";
import {
  Connection,
  CustomProviderResource,
  DatabaseProviderResource,
  InboundWebhookProviderResource,
  IntegrationProviderResource,
  isDatabaseProvider,
  ManifestIntegrationProviderResource,
  ResourceConfig,
  Resources,
} from "src/api/connectApi/types";
import { NodeTypeIcon } from "src/constants/NodeTypeIcons";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { AddNodeCard } from "src/nodeAdding/AddNodeCard";
import { SubPaneLayout } from "src/nodeAdding/SubPaneLayout";
import { isTemporarilyDisabledResource } from "src/router/featureFlags";
import { AddNodeParams } from "src/store/GraphStore";
import { compareStringsAscendingCaseInsensitive } from "src/utils/stringUtils";

export type AddResourcePaneProps = {
  connection: Connection;
  manifest?: ManifestIntegrationProvider;
  addNode: (params: AddNodeParams) => void;
  onBackButtonClick: () => void;
  onCloseButtonClick: () => void;
};

export const ConnectionResourceCards: React.FC<{
  connection: Connection;
  manifest?: ManifestIntegrationProvider;
  addNode: (params: AddNodeParams) => void;
}> = ({ connection, manifest, addNode }) => {
  const addNodeFromConnection = (
    connection: Connection,
    resourceConfig: ResourceConfig,
  ) => {
    if (connection.provider === "custom") {
      addNode({
        nodeType: NODE_TYPE.CUSTOM_CONNECTION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: resourceConfig.resource,
        } as CustomProviderResource,
        connectionId: connection.id,
        resourceConfigId: resourceConfig.id,
        mediaKey: connection.configuration.media_key as string,
      });
    } else if (isDatabaseProvider(connection.provider)) {
      addNode({
        nodeType: NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: resourceConfig.resource,
        } as DatabaseProviderResource,
        connectionId: connection.id,
        resourceConfigId: resourceConfig.id,
      });
    } else if (connection.provider === "webhook") {
      addNode({
        nodeType: NODE_TYPE.WEBHOOK_CONNECTION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: resourceConfig.resource,
        } as InboundWebhookProviderResource,
        connectionId: connection.id,
        resourceConfigId: resourceConfig.id,
        mediaKey: connection.configuration.media_key as string,
      });
    } else if (connection.manifest_version) {
      if (!manifest) {
        throw new Error("Manifest is required for manifest connections");
      }
      addNode({
        nodeType: NODE_TYPE.MANIFEST_CONNECTION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: resourceConfig.resource,
          manifest_version: connection.manifest_version,
        } as ManifestIntegrationProviderResource,
        connectionId: connection.id,
        resourceConfigId: resourceConfig.id,
        manifest,
      });
    } else {
      addNode({
        nodeType: NODE_TYPE.INTEGRATION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: resourceConfig.resource,
        } as IntegrationProviderResource,
        connectionId: connection.id,
        resourceConfigId: resourceConfig.id,
      });
    }
  };

  const getIconFromConnection = (
    connection: Connection,
    resourceConfig: ResourceConfig,
  ) => {
    if (connection.provider === "custom") {
      return (
        <NodeTypeIcon
          nodeType={NODE_TYPE.CUSTOM_CONNECTION_NODE}
          provider={connection.provider}
          size="lg"
        />
      );
    } else if (connection.provider === "webhook") {
      return (
        <NodeTypeIcon
          nodeType={NODE_TYPE.WEBHOOK_CONNECTION_NODE}
          provider={connection.provider}
          size="lg"
        />
      );
    } else {
      // Handling database and integration nodes
      return (
        <NodeTypeIcon
          nodeType={NODE_TYPE.INTEGRATION_NODE}
          provider={connection.provider}
          resource={resourceConfig.resource}
          size="lg"
        />
      );
    }
  };

  const renderLegacyProviderResourceConfigs = () => {
    return (
      <div>
        {connection.resource_configs
          .filter((resourceConfig) =>
            Resources.includes(resourceConfig.resource),
          )
          .filter((resourceConfig) => resourceConfig.resource !== "probe")
          .filter(
            (resourceConfig) =>
              !isTemporarilyDisabledResource(
                connection.provider,
                resourceConfig.resource,
              ),
          )
          .sort((a, b) =>
            compareStringsAscendingCaseInsensitive(a.name, b.name),
          )
          .map((resourceConfig) => (
            <AddNodeCard
              key={resourceConfig.id}
              description=""
              nodeIcon={getIconFromConnection(connection, resourceConfig)}
              title={resourceConfig.name}
              onClick={() => addNodeFromConnection(connection, resourceConfig)}
            />
          ))}
      </div>
    );
  };

  const renderManifestProviderResourceConfigs = () => {
    return (
      <div>
        {connection.resource_configs
          .sort((a, b) =>
            compareStringsAscendingCaseInsensitive(a.name, b.name),
          )
          .map((resourceConfig) => (
            <AddNodeCard
              key={resourceConfig.id}
              dataLoc={`add-${resourceConfig.resource}`}
              description=""
              nodeIcon={getIconFromConnection(connection, resourceConfig)}
              title={resourceConfig.name}
              onClick={() => addNodeFromConnection(connection, resourceConfig)}
            />
          ))}
      </div>
    );
  };

  const renderResourceConfigs = () => {
    if (connection.manifest_version) {
      return renderManifestProviderResourceConfigs();
    } else {
      return renderLegacyProviderResourceConfigs();
    }
  };

  return renderResourceConfigs();
};

export const AddResourcePane: React.FC<AddResourcePaneProps> = ({
  connection,
  manifest,
  addNode,
  onCloseButtonClick,
  onBackButtonClick,
}) => (
  <SubPaneLayout
    title="Choose a Resource"
    onBackButtonClick={onBackButtonClick}
    onCloseButtonClick={onCloseButtonClick}
  >
    <ConnectionResourceCards
      addNode={addNode}
      connection={connection}
      manifest={manifest}
    />
  </SubPaneLayout>
);
